import {
  doGetProfileDetails,
  doUpdateEmail,
  doGetUserPaymentMethod,
  doUpdateBillingAddress,
  doDeletePaymentMethod,
  doPostPaymentMethod,
  doGetPaymentHistory,
  doGetPaymentReceiptPdf,
  doUpdateProfileNames,
  doRequestNamesChange,
} from '@/api/profile';
import { isSuccessfulResponse } from '@/api/utilities';
import Vue from 'vue';
import { getFeatureFlagProvider, MILESTONE_USERS_PROVIDE_BOARD_ID_AND_DOB_ENABLED } from '@/lib/featureFlag';

const degreesNeedingFullBoardInformation = ['M.D.', 'D.O.', 'M.D., Ph.D.', 'M.D., MPH'];
const medicalBoardsWithOptionalBoardInformation = ['Self-Report to My Medical Board', 'I do not see my board'];

export default {
  namespaced: true,
  state: () => ({
    profile: null,
    paymentMethod: null,
    paymentMethodWillExpire: false,
    msgForAlertExpire: '',
    shouldShowCompleteProfileDialog: false,
  }),
  mutations: {
    setProfile(state, data) {
      state.profile = data;
    },
    setPaymentMethod(state, data) {
      state.paymentMethod = data;
    },
    setPaymentMethodWillExpire(state, data) {
      state.paymentMethodWillExpire = data;
    },
    setMsgForAlertExpire(state, data) {
      state.msgForAlertExpire = data;
    },
    setShouldShowCompleteProfileDialog(state, data) {
      state.shouldShowCompleteProfileDialog = data;
    },
  },
  actions: {
    async getProfile(context) {
      try {
        const response = await doGetProfileDetails();

        if (isSuccessfulResponse(response)) {
          Vue.prototype.$eventNotifier.setUser({
            currentPlanName: response.data.currentPlan?.planName ?? 'Free Trial',
          });

          context.commit('setProfile', response.data);
        }
      } catch (error) {
        context.commit('setProfile', null);
      }
    },
    async updateEmail(context, payload) {
      let response = await doUpdateEmail(payload);

      if (isSuccessfulResponse(response)) {
        await context.dispatch('auth/getUser', null, { root: true });
      }

      return response;
    },
    async getUserPaymentMethod(context) {
      let response = await doGetUserPaymentMethod();

      if (isSuccessfulResponse(response)) {
        context.commit('setPaymentMethod', response.data);
        return response.data;
      }

      return response.error;
    },
    async updateBillingAddress(context, payload) {
      let response = await doUpdateBillingAddress(payload);

      if (isSuccessfulResponse(response)) {
        return response.data;
      }

      return response.error;
    },
    async deletePaymentMethod(context) {
      let response = await doDeletePaymentMethod();

      if (isSuccessfulResponse(response)) {
        context.commit('setPaymentMethod', null);
        return response.data;
      }

      return response.error;
    },
    async addPaymentMethod(context, payload) {
      let response = await doPostPaymentMethod(payload);

      if (isSuccessfulResponse(response)) {
        context.commit('setPaymentMethod', response.data);
        return response.data;
      }

      return response.error;
    },
    async getPaymentHistory() {
      let response = await doGetPaymentHistory();

      if (isSuccessfulResponse(response)) {
        return response.data;
      }

      return response.error;
    },
    async getPaymentReceiptPdf(context, payload) {
      let response = await doGetPaymentReceiptPdf(payload);

      if (isSuccessfulResponse(response)) {
        return response.data;
      }

      return response.error;
    },
    async updateProfileNames(context, payload) {
      let response = await doUpdateProfileNames(payload);

      if (isSuccessfulResponse(response)) {
        await context.dispatch('getProfile');
        return response;
      }

      return response.error;
    },
    async checkIfPaymentMethodWillExpire(context) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const paymentMethod = await context.dispatch('getUserPaymentMethod');
      const YEAR_CYCLE = 12;

      if (paymentMethod.card) {
        const expirationMonth = paymentMethod.card.exp_month;
        const expirationYear = paymentMethod.card.exp_year;
        const expirationYearsToCurrent = expirationYear - currentYear;
        const expirationMonthsToCurrent = expirationMonth - currentMonth;

        if (currentYear > expirationYear) {
          context.commit('setPaymentMethodWillExpire', true);
          context.commit('setMsgForAlertExpire', 'Your payment method has already expired');
          return;
        }

        if (expirationYearsToCurrent <= 0 && expirationMonthsToCurrent < YEAR_CYCLE) {
          context.commit('setPaymentMethodWillExpire', true);
          context.commit('setMsgForAlertExpire', 'Your payment method will expire next billing cycle');
          return;
        }

        if (expirationYearsToCurrent <= 1 && expirationMonthsToCurrent <= 0) {
          context.commit('setPaymentMethodWillExpire', true);
          context.commit('setMsgForAlertExpire', 'Your payment method will expire next billing cycle');
          return;
        }

        context.commit('setPaymentMethodWillExpire', false);
      } else {
        context.commit('setPaymentMethodWillExpire', false);
      }
    },
    async requestNamesChange(context, payload) {
      let response = await doRequestNamesChange(payload);

      if (isSuccessfulResponse(response)) {
        return response.data;
      }

      return response.error;
    },
    unsetProfile(context) {
      context.commit('setProfile', null);
    },
    showCompleteProfileDialog(context) {
      context.commit('setShouldShowCompleteProfileDialog', true);
    },
    hideCompleteProfileDialog(context) {
      context.commit('setShouldShowCompleteProfileDialog', false);
    },
    unsetPaymentWillExpire(context) {
      context.commit('setPaymentMethodWillExpire', false);
      context.commit('setMsgForAlertExpire', '');
    },
  },
  getters: {
    currentProfile: (state) => {
      return state.profile;
    },
    currentPaymentMethod: (state) => {
      return state.paymentMethod;
    },
    willPaymentMethodExpire: (state) => {
      return state.paymentMethodWillExpire;
    },
    getMsgForAlertExpire: (state) => {
      return state.msgForAlertExpire;
    },
    hasIncompleteProfile: (
      state,
      { userDegreeNeedsFullBoardInformation, userMedicalBoardNeedsFullBoardInformation, hasIncompleteBoardInformation },
    ) => {
      const milestoneUsersProvideBoardIdAndDobEnabled = getFeatureFlagProvider().isFeatureEnabled(
        MILESTONE_USERS_PROVIDE_BOARD_ID_AND_DOB_ENABLED,
      );

      if (!milestoneUsersProvideBoardIdAndDobEnabled) return false;
      if (!userDegreeNeedsFullBoardInformation) return false;
      if (!userMedicalBoardNeedsFullBoardInformation) return false;

      return hasIncompleteBoardInformation;
    },
    userDegreeNeedsFullBoardInformation: (state, { currentProfile }) => {
      return degreesNeedingFullBoardInformation.includes(currentProfile?.degree);
    },
    userMedicalBoardNeedsFullBoardInformation: (state, { currentProfile }) => {
      return !medicalBoardsWithOptionalBoardInformation.includes(currentProfile?.medicalBoard?.name);
    },
    hasIncompleteBoardInformation: (state, { hasBoardId, hasDateOfBirth }) => {
      return !hasBoardId || !hasDateOfBirth;
    },
    hasBoardId(state, { currentProfile }) {
      return !!currentProfile?.medicalBoard?.board_number;
    },
    hasDateOfBirth(state, { currentProfile }) {
      const dateOfBirth = currentProfile?.dateOfBirth;
      return !!dateOfBirth && dateOfBirth !== 'Unknown';
    },
    shouldShowCompleteProfileDialog: (state) => {
      return state.shouldShowCompleteProfileDialog;
    },
  },
};
